import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plug } from 'lucide-react';
import api from '../services/api';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import { useAuthStore } from '../stores/authStore';

export default function GlossaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [searchQuery, setSearchQuery] = useState('');
  const [accessibleTasks, setAccessibleTasks] = useState<Set<string>>(new Set());
  const [accessibleAgents, setAccessibleAgents] = useState<{ bq_dataset?: string; skills?: string[] }[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadTheme();
    loadAccessibleDatasets();
  }, []);

  // Links like /glossary#claude-copilot (from the login announcement) open at that section.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [location.hash]);

  const loadTheme = () => {
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute('data-theme', saved);
    }
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fm-theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const loadAccessibleDatasets = async () => {
    try {
      const res = await api.get('/api/datasets');
      const agents = res.data.agents || [];
      setIsAdmin(res.data.is_admin || false);
      setAccessibleTasks(new Set(agents.map((a: any) => a.task_name)));
      setAccessibleAgents(agents);
    } catch (err) {
      // No fallback: if access can't be checked, show no verticals or data sources.
      console.error('Failed to load datasets:', err);
      setIsAdmin(false);
      setAccessibleTasks(new Set());
      setAccessibleAgents([]);
    }
  };

  const coreMetrics = [
    {
      abbr: 'EPC',
      name: 'Earnings Per Clickout',
      formula: 'EPC = (SUM(revenue) + SUM(IFNULL(bonus, 0))) ÷ Clickouts',
      desc: 'Average revenue earned per on-site click to a partner site (journeys excluded). Numerator is default revenue WITH bonus — same as Total Revenue unless the user explicitly excludes bonus. Tip: EPC can rise even when total revenue falls if low-quality traffic is removed — always check both together.',
      tag: 'Primary KPI',
      color: 'var(--accent)',
      searchTerms: 'epc earnings per click revenue clickouts'
    },
    {
      abbr: 'EPV',
      name: 'Earnings Per Visit',
      formula: 'EPV = (SUM(revenue) + SUM(IFNULL(bonus, 0))) ÷ Sessions',
      desc: 'Average revenue generated per session on site. Previously referred to as RPS (Revenue Per Session) — EPV is the current standard name. Numerator is default revenue WITH bonus. Note: Sessions = COUNT(DISTINCT session_id) over the full period — never sum daily session counts.',
      tag: 'Efficiency',
      color: 'var(--green)',
      searchTerms: 'rps revenue per session sessions epv earnings per visit'
    },
    {
      abbr: 'ARPC',
      name: 'Avg. Revenue Per Conversion',
      formula: 'ARPC = Revenue ÷ Conversions',
      desc: 'Average payout value when a clickout results in a conversion (lead, sale, or qualified action). Useful for diagnosing divergences between revenue and conversion trends. Pattern: Revenue ↑ + Conversions ↓ = ARPC ↑ (higher-value mix). Always check ARPC when these two metrics diverge.',
      tag: 'Diagnostic',
      color: 'var(--yellow)',
      searchTerms: 'arpc average revenue per conversion conversions'
    },
    {
      abbr: 'Revenue',
      name: 'Total Revenue',
      formula: 'Revenue = SUM(revenue) + SUM(IFNULL(bonus, 0))',
      desc: 'The default revenue figure — always WITH bonus. There is no revenue_with_bonus column; compute it. Exclude bonus (opt-in only): SUM(revenue) — use only when the user explicitly says "exclude bonus", "no bonus", or "base revenue". Label clearly. How much bonus? SUM(bonus) — $0 is a valid answer. Tier Revenue: SUM(tier_revenue) — a separate stream, never added to the default total unless specified.',
      tag: 'Financial',
      color: 'var(--blue)',
      searchTerms: 'revenue money total earnings bonus'
    },
    {
      abbr: 'Clickouts',
      name: 'Clickouts to Partner Sites',
      formula: 'COUNT(DISTINCT CASE WHEN event IN (\'conversion\',\'add_to_cart\') AND url NOT LIKE journey% THEN tid END)',
      desc: 'On-site click events that send a user to a partner/advertiser site. Journey URLs are excluded. Unique Clickouts: same filter but COUNT(DISTINCT session_id) — number of sessions with at least one clickout.',
      tag: 'Volume',
      color: 'var(--purple)',
      searchTerms: 'clickouts clicks tid transactions partner'
    },
    {
      abbr: 'Conversions',
      name: 'Conversion Count',
      formula: 'COUNT(DISTINCT non_reattributed_tid) + SUM(IFNULL(reattributed_sale, 0))',
      desc: 'Total revenue-generating actions on sort gold tables. Uses two dedicated columns: non_reattributed_tid for standard conversions and reattributed_sale for reattributed ones. Always an integer — never reported with decimals. Period rule: Always compute over the full date range in a single query — never sum daily counts.',
      tag: 'Outcome',
      color: 'var(--green)',
      searchTerms: 'conversions leads sales qualified actions cv non_reattributed reattributed'
    },
    {
      abbr: 'WoW %',
      name: 'Week-over-Week Change',
      formula: 'ROUND(SAFE_DIVIDE(curr - prior, prior) * 100, 1)',
      desc: 'Percentage change comparing the current period to the same period 7 days prior. The default comparison period for single-metric questions. Always shown with sign and 1 decimal place (e.g. +18.3%, -6.0%).',
      tag: 'Period',
      color: 'var(--accent)',
      searchTerms: 'wow week over week change growth percentage trend'
    },
    {
      abbr: 'MoM %',
      name: 'Month-over-Month Change',
      formula: 'MTD vs same MTD window prior month',
      desc: 'Compares month-to-date figures with the same number of days in the prior month. Note: MTD and L7D trends can diverge if month-start vs recent-week performance differ — the agent flags this gap explicitly.',
      tag: 'Period',
      color: 'var(--yellow)',
      searchTerms: 'mom month over month change growth comparison mtd l7d'
    },
    {
      abbr: 'Sessions',
      name: 'Unique Sessions',
      formula: 'Sessions = COUNT(DISTINCT session_id)',
      desc: 'Unique user sessions on site over a given period. Always computed over the full date range — never summed from daily session counts (a session can span midnight).',
      tag: 'Volume',
      color: 'var(--blue)',
      searchTerms: 'sessions visits session_id unique users'
    },
    {
      abbr: 'EPUC',
      name: 'Earnings Per Unique Clickout',
      formula: 'EPUC = (SUM(revenue) + SUM(IFNULL(bonus, 0))) ÷ Unique Clickouts',
      desc: 'Average revenue per session that had at least one clickout. Unique Clickouts = COUNT(DISTINCT session_id) using the same clickout filter (journeys excluded). Numerator is default revenue WITH bonus.',
      tag: 'Efficiency',
      color: 'var(--accent)',
      searchTerms: 'epuc earnings per unique clickout unique clickouts session'
    },
    {
      abbr: 'CTJ',
      name: 'Click to Journeys',
      formula: 'CTJ = COUNT(DISTINCT tid) where click_url LIKE journey%',
      desc: 'Total clicks to internal journey pages (the inverse of standard Clickouts). Journey URLs include /journey/, pet.advisor.forbes, comparecoverage.com, journey.forbes. UCTJ (Unique CTJ): COUNT(DISTINCT session_id) for the same filter — sessions that initiated a journey click.',
      tag: 'Volume',
      color: 'var(--purple)',
      searchTerms: 'ctj click journey journeys internal navigation'
    },
    {
      abbr: 'CTR',
      name: 'Click Through Rate',
      formula: 'CTR = Clickouts ÷ Sessions',
      desc: 'Proportion of sessions that resulted in at least one clickout to a partner site. Also referred to as Forbes Conversion Rate. A higher CTR means visitors are more likely to click through to partners.',
      tag: 'Efficiency',
      color: 'var(--green)',
      searchTerms: 'ctr click through rate conversion rate forbes sessions clickouts'
    }
  ];

  const trafficMediums = [
    { value: 'CPC (Paid Search)', aliases: 'Paid Search · Cost per Click · SEM · CPC', notes: 'Typically highest EPC; Google + Bing paid' },
    { value: 'Paid Generic', aliases: 'Paid Search · Cost per Click · SEM · CPC', notes: 'Broad paid search; report alongside CPC (Paid Search) when user says "SEM"' },
    { value: 'Organic', aliases: 'Organic · SEO · Natural Search', notes: 'Unpaid search; seasonal YoY patterns common' },
    { value: 'Paid Native', aliases: 'Paid Native · Native · Native Ads', notes: 'Taboola, Outbrain — lower EPC, higher volume' },
    { value: 'Native', aliases: 'Native', notes: '' },
    { value: 'Paid Social', aliases: 'Paid Social · Social Ads', notes: 'Facebook/Meta, TikTok, Reddit' },
    { value: 'Social', aliases: 'Social', notes: 'Organic social media' },
    { value: 'Email', aliases: 'Email · CRM', notes: 'Newsletter / lifecycle campaigns; send-day spikes' },
    { value: 'CRM', aliases: 'CRM · Email', notes: '' },
    { value: 'Referral', aliases: 'Referral', notes: '' },
    { value: 'Partner Referral', aliases: 'Referral', notes: '' },
    { value: 'RSS', aliases: 'Referral', notes: '' },
    { value: 'Recirc', aliases: 'Referral', notes: 'On-site recirculation links' },
    { value: 'Internal', aliases: 'Internal · Testing', notes: 'Exclude from production reporting' },
    { value: 'Unattributable', aliases: 'Unattributable', notes: 'Revenue received; click not matched' },
    { value: 'Other', aliases: 'Other', notes: '' }
  ];

  // Each source lists the agents that read it, so users only see sources behind their agents.
  type AgentSource = { bq_dataset?: string; skills?: string[] };
  const dataSources = [
    {
      layer: 'Gold Agentic',
      usedBy: (a: AgentSource) => !!a.bq_dataset?.startsWith('fm_sv_'),
      table: 'fm-gold-layer…tbl_agentic_analyst_sort_*',
      useCase: "Pre-filtered, optimised for EPC/RPS trend analysis. Agent's primary source."
    },
    {
      layer: 'Gold Reporting',
      usedBy: (a: AgentSource) => a.bq_dataset === 'fm_reporting_internal',
      table: 'fm-gold-layer…fm_reporting_internal.*',
      useCase: 'Revenue, EPC, cross-sell, users — multiple grain tables joined via CTEs'
    },
    {
      layer: 'Silver Reattributed',
      usedBy: (a: AgentSource) => a.bq_dataset === 'fm_revenue_attribution',
      table: 'sort_master_reattributed',
      useCase: 'Raw event-level data. Used for ad-hoc validation. Numbers will differ from gold layer.'
    },
    {
      layer: 'Demand Pulse',
      usedBy: (a: AgentSource) => !!a.skills?.includes('demand_pulse'),
      table: 'fm-microservices…demand_pulse_*',
      useCase: 'Google search demand by market (keywords, mindsets, forecasts) — enable the Demand Pulse skill on an agent'
    },
    {
      layer: 'Social Native',
      usedBy: (a: AgentSource) => a.bq_dataset === 'social_native',
      table: 'fm-gold-layer…social_native.meta_daily_conversion_v2',
      useCase: 'Meta paid social daily conversions'
    },
    {
      layer: 'GCP Billing',
      usedBy: (a: AgentSource) => a.bq_dataset === 'billing',
      table: 'fm-silver-layer…billing.gcp_billing_export_v1_*',
      useCase: 'Cloud cost analysis by service, project, SKU'
    }
  ];

  const verticals = [
    { name: 'Diet & Weight Loss', taskName: 'sort_diet_weight_loss_daily', bqSource: 'tbl_agentic_analyst_sort_diet_weight_loss' },
    { name: 'Debt Relief', taskName: 'sort_debt_relief_daily', bqSource: 'tbl_agentic_analyst_sort_debt_relief' },
    { name: 'Credit Cards', taskName: 'sort_credit_cards_daily', bqSource: 'tbl_agentic_analyst_sort_credit_cards' },
    { name: 'Auto Insurance', taskName: 'sort_auto_insurance_daily', bqSource: 'tbl_agentic_analyst_sort_auto_insurance' },
    { name: 'Pet Insurance', taskName: 'sort_pet_insurance_daily', bqSource: 'tbl_agentic_analyst_sort_pet_insurance' },
    { name: 'Travel Insurance', taskName: 'sort_travel_insurance_daily', bqSource: 'tbl_agentic_analyst_sort_travel_insurance' },
    { name: 'GCP Billing', taskName: 'gcp_billing_daily', bqSource: 'fm-silver-layer billing export' }
  ];

  const abbreviations = {
    debtRelief: [
      { key: 'NDR', value: 'National Debt Relief' },
      { key: 'ADR', value: 'Accredited Debt Relief' },
      { key: 'FDR', value: 'Freedom Debt Relief' },
      { key: 'PDR', value: 'Pacific Debt Relief' },
      { key: 'CDS', value: 'Consumer Debt Solutions' },
      { key: 'CuradebtA/B', value: 'Curadebt (variant A/B)' }
    ],
    insurance: [
      { key: 'USAA', value: 'USAA Insurance' },
      { key: 'GEICO', value: 'GEICO Auto Insurance' },
      { key: 'PRG', value: 'Progressive' },
      { key: 'SFM', value: 'State Farm' }
    ],
    general: [
      { key: 'BQ', value: 'BigQuery' },
      { key: 'L7D', value: 'Last 7 days' },
      { key: 'WTD', value: 'Start of current week (Mon) to latest date' },
      { key: 'MTD', value: 'Start of current month (1st) to latest date' },
      { key: 'QTD', value: 'Start of current quarter to latest date' },
      { key: 'YTD', value: 'Start of current year (1 Jan) to latest date' },
      { key: 'WoW', value: 'Current WTD vs same window the week before' },
      { key: 'MoM', value: 'Current MTD vs same days in prior month' },
      { key: 'QoQ', value: 'Current QTD vs same days in prior quarter' },
      { key: 'YoY', value: 'Current YTD vs same days in prior year' },
      { key: 'Q1', value: '1 Jan – 31 Mar' },
      { key: 'Q2', value: '1 Apr – 30 Jun' },
      { key: 'Q3', value: '1 Jul – 30 Sep' },
      { key: 'Q4', value: '1 Oct – 31 Dec' },
      { key: 'CV', value: 'Conversions' },
      { key: 'CTP', value: 'Click to Partner (= Clickouts)' },
      { key: 'tid', value: 'Transaction ID (click)' },
      { key: 'CPC', value: 'Cost Per Click' },
      { key: 'SEM', value: 'Search Engine Marketing' },
      { key: 'SEO', value: 'Search Engine Optimisation' },
      { key: 'EPV', value: 'Earnings Per Visit (= revenue ÷ sessions)' },
      { key: 'EPUC', value: 'Earnings Per Unique Clickout' },
      { key: 'CTJ', value: 'Click to Journeys' },
      { key: 'UCTJ', value: 'Unique Click to Journeys' },
      { key: 'CTR', value: 'Click Through Rate (Clickouts ÷ Sessions)' }
    ]
  };

  const filteredMetrics = coreMetrics.filter(m =>
    !searchQuery || 
    m.searchTerms.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.abbr.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const shouldShowVertical = (taskName: string) => {
    return isAdmin || accessibleTasks.has(taskName);
  };

  const visibleDataSources = dataSources.filter((ds) => isAdmin || accessibleAgents.some(ds.usedBy));

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: '8px' }}>
            ON THIS PAGE
          </div>
          <nav>
            {[
              { id: 'claude-copilot', label: 'Claude & Copilot', color: 'var(--accent)' },
              { id: 'core-metrics', label: 'Core Metrics', color: 'var(--accent)' },
              { id: 'traffic-channels', label: 'Traffic Channels', color: 'var(--green)' },
              { id: 'data-sources', label: 'Data Sources', color: 'var(--blue)' },
              { id: 'verticals', label: 'Verticals', color: 'var(--purple)' },
              { id: 'abbreviations', label: 'Abbreviations', color: 'var(--red)' }
            ].map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  transition: 'background 0.15s, color 0.15s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--surface2)';
                  e.currentTarget.style.color = 'var(--text)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text-muted)';
                }}
              >
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: section.color,
                  flexShrink: 0
                }} />
                {section.label}
              </a>
            ))}
          </nav>
        </div>

        <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 10px',
              borderRadius: '8px',
              fontSize: '13px',
              transition: 'background 0.15s, color 0.15s',
              fontFamily: 'inherit'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--surface2)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Back to Agent
          </button>
        </div>
      </Sidebar>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          user={user}
        />
        
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 40px 80px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
          {/* Header */}
          <div style={{
            padding: '12px 0 32px',
            borderBottom: '1px solid var(--border)',
            marginBottom: '40px'
          }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              margin: 0
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: '#8b5cf618',
                border: '1px solid var(--accent-dim)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '22px', height: '22px', color: 'var(--accent)' }}>
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  <line x1="9" y1="9" x2="15" y2="9"/>
                  <line x1="9" y1="13" x2="13" y2="13"/>
                </svg>
              </div>
              Metric Glossary
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '10px' }}>
              Definitions for every metric, channel, and abbreviation used by the Data Agent. Use this as your reference when interpreting agent responses or building your own questions.
            </p>
          </div>

        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '10px 14px',
          marginBottom: '32px',
          transition: 'border-color 0.15s'
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', color: 'var(--text-muted)', flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search metrics, abbreviations, channels…"
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              fontFamily: 'var(--font)',
              color: 'var(--text)'
            }}
          />
        </div>

        {/* Claude & Copilot */}
        <section id="claude-copilot" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#8b5cf618',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Plug size={20} />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Use Pearson in Claude & Copilot</h2>
          </div>

          <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 20px' }}>
            Pearson works in three places with the same data and access. Use this web app for the full experience,
            or the <strong style={{ color: 'var(--text)' }}>Pearson FA MCP</strong> connector to use Pearson inside Claude
            or Microsoft 365 Copilot.
          </p>

          <div style={{
            padding: '16px 18px',
            background: '#8b5cf610',
            border: '1px solid var(--accent)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: '12px',
            fontSize: '13px',
            lineHeight: 1.65,
            marginBottom: '20px'
          }}>
            <strong style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--accent)'
            }}>
              Before you begin
            </strong>
            Request access to <strong>Pearson Data Analyst (Production)</strong> in the Microsoft My Access portal with a short
            business justification (team, datasets, work), and wait for the <strong>Approved</strong> email before connecting
            Claude or Copilot. Use the same organizational account everywhere.
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {[
              {
                title: 'Claude',
                tag: 'Pearson FA MCP connector',
                steps: [
                  'If you connected Pearson FA MCP before, disconnect it first.',
                  'Open Claude and sign in with your organizational account.',
                  'Go to Settings → Connectors (or Customize → Connectors).',
                  'Select the approved Pearson FA MCP connector, then Connect.',
                  'Complete the Microsoft sign-in and consent.',
                  'Back in Claude, check the connector shows as connected.',
                ],
              },
              {
                title: 'Microsoft 365 Copilot',
                tag: 'Pearson FA MCP',
                steps: [
                  'Open Copilot (web or desktop) and select More agents.',
                  'Search for Pearson FA MCP and add it.',
                  'Not listed? Refresh the web app or restart the desktop app, then check Your agents.',
                  'Open Pearson FA MCP and send “Hi” to start it.',
                  'Complete the Microsoft sign-in and consent if asked.',
                  'Return to the chat and select Retry if Copilot asks.',
                ],
              },
            ].map((app) => (
              <div key={app.title} style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '18px 20px'
              }}>
                <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '2px' }}>{app.title}</div>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '12px',
                  color: 'var(--accent)',
                  marginBottom: '12px'
                }}>{app.tag}</div>
                <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', lineHeight: 1.65, color: 'var(--text-muted)' }}>
                  {app.steps.map((step) => <li key={step}>{step}</li>)}
                </ol>
              </div>
            ))}
          </div>

          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '16px 20px',
            fontSize: '13px',
            lineHeight: 1.65,
            color: 'var(--text-muted)'
          }}>
            <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: '4px' }}>Getting good answers</div>
            Start by asking which datasets are available to you. Then use the pattern:
            question + dataset + date range + metric + filters + output format, e.g.
            <em> “Compare revenue for the last 7 days with the 7 days before, by traffic medium, as a table.”</em>{' '}
            Check important figures against an approved source before you share them.
            <div style={{ marginTop: '10px' }}>
              <a
                href="https://marketplaceco-my.sharepoint.com/:w:/r/personal/achoudhary_marketplace_co/_layouts/15/Doc.aspx?sourcedoc=%7B29A7E918-2CED-473E-A35A-0944415F5B06%7D&file=Pearson%20User%20Manual.docx&action=default"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
              >
                Open the full user manual →
              </a>
              <span> · Still stuck? Email </span>
              <a href="mailto:snakos@marketplace.co" style={{ color: 'var(--accent)', textDecoration: 'none' }}>snakos@marketplace.co</a>
            </div>
          </div>
        </section>

        {/* Core Metrics */}
        <section id="core-metrics" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#8b5cf618',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '20px', height: '20px' }}>
                <path d="M3 3v18h18"/><path d="M18.4 9l-6-6-4 4-4-4"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Core Metrics</h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {filteredMetrics.map((metric, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(90deg, ${metric.color}, transparent)` }} />
                <div style={{
                  fontSize: '20px',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  marginBottom: '4px',
                  color: metric.color
                }}>
                  {metric.abbr}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-muted)',
                  marginBottom: '10px',
                  fontWeight: 500
                }}>
                  {metric.name}
                </div>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '11px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  marginBottom: '10px',
                  color: '#c4c8e6',
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {metric.formula}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  lineHeight: 1.6
                }}>
                  {metric.desc}
                </div>
                <span style={{
                  display: 'inline-block',
                  marginTop: '10px',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  padding: '2px 7px',
                  borderRadius: '4px',
                  background: metric.color + '20',
                  color: metric.color,
                  border: `1px solid ${metric.color}40`
                }}>
                  {metric.tag}
                </span>
              </div>
            ))}
          </div>
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '32px 0' }} />

        {/* Traffic Channels */}
        <section id="traffic-channels" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#34d39918',
              color: 'var(--green)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '20px', height: '20px' }}>
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Traffic Channels</h2>
          </div>

          <div style={{
            padding: '16px 18px',
            background: '#8b5cf610',
            border: '1px solid var(--accent)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: '12px',
            fontSize: '13px',
            lineHeight: 1.65,
            marginBottom: '20px'
          }}>
            <strong style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--accent)'
            }}>
              Display Rule
            </strong>
            Values in the <code>traffic_medium</code> column are proper-cased. Always display them exactly as stored — never lowercase or remap. Use <code>traffic_source</code> for source-level breakdowns.
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>traffic_medium value</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Common Language / Aliases</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {trafficMediums.map((tm, i) => (
                  <tr key={i}>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === trafficMediums.length - 1 ? 'none' : '1px solid #2e314820',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}>{tm.value}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === trafficMediums.length - 1 ? 'none' : '1px solid #2e314820',
                      lineHeight: 1.5
                    }}>{tm.aliases}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === trafficMediums.length - 1 ? 'none' : '1px solid #2e314820',
                      lineHeight: 1.5,
                      color: 'var(--text-muted)'
                    }}>{tm.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '32px 0' }} />

        {/* Data Sources */}
        <section id="data-sources" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#60a5fa18',
              color: 'var(--blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '20px', height: '20px' }}>
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Data Sources</h2>
          </div>

          <div style={{
            padding: '16px 18px',
            background: '#60a5fa10',
            border: '1px solid var(--blue)',
            borderLeft: '3px solid var(--blue)',
            borderRadius: '12px',
            fontSize: '13px',
            lineHeight: 1.65,
            marginBottom: '20px'
          }}>
            <strong style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--blue)'
            }}>
              Why numbers may differ from Looker / dashboards
            </strong>
            The agent queries gold-layer agentic tables optimised for trend analysis. These apply different conversion attribution and deduplication logic than the silver-layer reattributed tables used by some dashboards. The difference is expected — not a data error.
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Layer</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Table Pattern</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Use Case</th>
                </tr>
              </thead>
              <tbody>
                {visibleDataSources.map((ds, i) => (
                  <tr key={i}>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === visibleDataSources.length - 1 ? 'none' : '1px solid #2e314820',
                      fontWeight: 600
                    }}>{ds.layer}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === visibleDataSources.length - 1 ? 'none' : '1px solid #2e314820',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '12px'
                    }}>{ds.table}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === visibleDataSources.length - 1 ? 'none' : '1px solid #2e314820',
                      lineHeight: 1.5
                    }}>{ds.useCase}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '32px 0' }} />

        {/* Verticals */}
        <section id="verticals" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#a78bfa18',
              color: 'var(--purple)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '20px', height: '20px' }}>
                <rect x="2" y="3" width="6" height="18" rx="1"/>
                <rect x="9" y="8" width="6" height="13" rx="1"/>
                <rect x="16" y="5" width="6" height="16" rx="1"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Verticals & Datasets</h2>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Vertical</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>Task Name</th>
                  <th style={{
                    background: 'var(--surface2)',
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)'
                  }}>BigQuery Source</th>
                </tr>
              </thead>
              <tbody>
                {verticals
                  .filter(v => shouldShowVertical(v.taskName))
                  .map((v, i, arr) => (
                  <tr key={i}>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid #2e314820',
                      fontWeight: 600
                    }}>{v.name}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid #2e314820',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '12px'
                    }}>{v.taskName}</td>
                    <td style={{
                      padding: '10px 16px',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid #2e314820',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '12px'
                    }}>{v.bqSource}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '32px 0' }} />

        {/* Abbreviations */}
        <section id="abbreviations" style={{ marginBottom: '48px', scrollMarginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#f8717118',
              color: 'var(--red)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '20px', height: '20px' }}>
                <polyline points="4 7 4 4 20 4 20 7"/>
                <line x1="9" y1="20" x2="15" y2="20"/>
                <line x1="12" y1="4" x2="12" y2="20"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Provider Abbreviations</h2>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.6 }}>
            The agent recognises these common shorthand names and maps them automatically to the full provider name in BigQuery queries.
          </p>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: '12px' }}>Debt Relief</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {abbreviations.debtRelief.map((abbr, i) => (
                <div key={i} style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '12px 14px'
                }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    marginBottom: '2px'
                  }}>{abbr.key}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{abbr.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: '12px' }}>Insurance</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {abbreviations.insurance.map((abbr, i) => (
                <div key={i} style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '12px 14px'
                }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    marginBottom: '2px'
                  }}>{abbr.key}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{abbr.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: '12px' }}>General</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {abbreviations.general.map((abbr, i) => (
                <div key={i} style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '12px 14px'
                }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    marginBottom: '2px'
                  }}>{abbr.key}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{abbr.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
          </div>
        </div>
      </main>
    </div>
  );
}
